import { useEffect, useRef, useState } from 'react';

// The export preview's captions PLAY: words light up at the clip's own word times, the way the
// phone app's preview does and the worker burns them (caption_fx). The numbers below are the
// worker's (caption_fx.fx_events): keep them in step.

export const FX = {
  popUp: 0.09, popSettle: 0.18, popPeak: 1.24, popRest: 1.14,     // \t(0,90,124%) \t(90,180,114%)
  slide: 0.14, slideFade: 0.12, slideDrop: 0.35,                   // \move(… +0.35·size …,0,140)\fad(120,0)
  fade: 0.16,                                                      // \fad(160,0)
  hold: 0.8,                                                       // the last word stays up before the loop starts again
};

/** When each of `n` words starts (seconds from the clip's start): the real times when there is
 *  one per word, else spread evenly over the clip (an ✏ edit with a different word count). */
export const wordTimes = (n, starts, seconds) => {
  if (!n) return [];
  if (Array.isArray(starts) && starts.length === n && starts.every((s) => Number.isFinite(s))) {
    return starts.map((s) => Math.max(0, s));
  }
  const span = seconds > 0 ? seconds : n * 0.4;
  return Array.from({ length: n }, (_, i) => (i * span) / n);
};

/** The word being said at `t` (the last one started; the first before any) and how far
 *  through it we are (0–1), its age in seconds. */
export const wordAt = (times, t, seconds) => {
  if (!times.length) return { k: -1, progress: 0, age: 0 };
  let k = 0;
  for (let i = 0; i < times.length; i += 1) if (times[i] <= t) k = i;
  const end = k + 1 < times.length ? times[k + 1] : Math.max(times[k] + 0.3, seconds || 0);
  const age = Math.max(0, t - times[k]);
  return { k, age, progress: Math.min(1, age / Math.max(0.05, end - times[k])) };
};

/** [first, last+1) of the words on screen while word k is said — the worker's groups. */
export const shownRange = (k, n, mode, wordsPerGroup) => {
  if (n <= 0) return [0, 0];
  const i = Math.max(0, Math.min(k, n - 1));
  if (mode === 'word') return [i, i + 1];
  if (mode === 'group') {
    const g = Math.max(1, wordsPerGroup || 1);
    const g0 = Math.floor(i / g) * g;
    return [g0, Math.min(n, g0 + g)];
  }
  return [0, n];
};

/** The lit word's motion at `age` seconds: { transform, opacity } (or {}). */
export const wordMotion = (highlight, reveal, age, entering) => {
  const out = {};
  const parts = [];
  if (entering && reveal === 'slide') {
    parts.push(`translateY(${(FX.slideDrop * (1 - Math.min(1, age / FX.slide))).toFixed(3)}em)`);
    out.opacity = Math.min(1, age / FX.slideFade);
  } else if (entering && reveal === 'fade') {
    out.opacity = Math.min(1, age / FX.fade);
  }
  if (highlight === 'pop') {
    const s = age < FX.popUp
      ? 1 + (FX.popPeak - 1) * (age / FX.popUp)
      : age < FX.popSettle
        ? FX.popPeak - (FX.popPeak - FX.popRest) * ((age - FX.popUp) / (FX.popSettle - FX.popUp))
        : FX.popRest;
    parts.push(`scale(${s.toFixed(3)})`);
  } else if (highlight === 'wiggle') {
    // \frz7 → \t(0,110,-7) → \t(110,220,4) → \t(220,320,0)   (ASS turns the other way round)
    const keys = [[0, 7], [0.11, -7], [0.22, 4], [0.32, 0]];
    let deg = 0;
    for (let i = 0; i < keys.length - 1; i += 1) {
      const [a, da] = keys[i];
      const [b, db] = keys[i + 1];
      if (age >= a && age < b) deg = da + (db - da) * ((age - a) / (b - a));
    }
    parts.push(`rotate(${(-deg).toFixed(2)}deg)`);
  }
  if (parts.length) {
    out.transform = parts.join(' ');
    out.display = 'inline-block';
  }
  return out;
};

/**
 * Seconds into the clip, ticking every animation frame: from `getTime()` when given (the clip
 * playing in the preview), else a loop of `loop` seconds. Off (`active` false) → 0.
 */
export const useClipClock = (active, loop, getTime = null) => {
  const [t, setT] = useState(0);
  const getRef = useRef(getTime);
  getRef.current = getTime;
  useEffect(() => {
    if (!active || typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      setT(0);
      return undefined;
    }
    let raf;
    let lastFrame = -1;
    const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const tick = (now) => {
      const get = getRef.current;
      // (a frame's timestamp can be a hair EARLIER than t0 — never let time run negative)
      const value = get ? Math.max(0, get() || 0) : (Math.max(0, (now - t0) / 1000) % Math.max(0.5, loop));
      const frame = Math.floor(value * 60);
      if (frame !== lastFrame) {
        lastFrame = frame;
        setT(value);
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [active, loop]);
  return t;
};
